import {
	Button,
	CancellableEvent,
	error,
	useIsFeatureEnabled,
	success,
	Text,
	WarningDialog,
	NavigationPrompt,
	useCompanyDateTime,
	decoratedAttributes,
	getSubscriptions,
} from '@innovyze/stylovyze';
import { useAuth0 } from '@innovyze/stylovyze';
import { viewerAccess } from '@innovyze/shared-utils';
import {
	Category,
	Component,
	ComponentType,
	RiskCalcStatusResult,
	RiskConfig,
} from '@Types';
import { RenameRisk, AddEditComponent } from '@Components';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	selectRiskTab,
	selectLofStore,
	selectCofStore,
	selectRiskStore,
	selectIsRiskSetupDirty,
	selectRiskStatus,
	selectRiskLastRun,
	selectRiskRunning,
	selectDidMatrixUpdate,
	selectRiskConfig,
	selectRiskSetting,
	selectEnablePreview,
	selectUpdatedRiskMatrixGrid,
	selectUpdatedRiskRanges,
	selectOverallRisk,
	selectRiskDetailsQueryObj,
	selectRiskDetailsStartSearch,
	selectRiskDetailsSearch,
} from '@Selectors';
import {
	saveLoF,
	saveCoF,
	setSaveRun,
	getLog,
	reloadRiskDetails,
	runRisk,
	setRunCompleteReload,
	recordRunCompleted,
	getRiskConfig,
	getCustomTableFields,
	getProximityAttributeFields,
	getCoFFields,
	getSpatialLayerList,
	getLoFFields,
	getLoFInspectionFields,
	setRiskStatus,
	setRiskLastRun,
	getRiskDetailsDefaulted,
	setRefreshList,
	resetRisk,
	setRiskRunning,
	getSimulationData,
	setDidMatrixUpdate,
	saveRiskConfigAction,
	setRiskSetupDirty,
	setMatrixPreviewStatus,
	getTaskData,
	initRiskMatrixPreview,
	setOnlyGradesUpdated,
	cancelRiskMatrixPreview,
	clearRiskScanId,
	setRiskDetailsIsBusy,
	getRiskDetailsRejected,
	setSearchStart,
	getRiskDetailsWSReseived,
	getDeteriorationModels,
	getFailureDefinitions,
} from '@Actions';
import RiskCalcStatus from '@Components/RiskCalcStatus/RiskCalcStatus';
import { useWebSocketRoute } from '@Hooks/useWebSocketRoute';
import { runStatusToastAction } from '@Utils/riskRunStatusToast';
import { resolveStatusConfigId } from '@Utils/riskCalcStatusSubscriptions';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { addRouteNamespace } from '@Utils/actions';
import { DialogProps, Grid } from '@mui/material';
import { selectRiskConfigsPage } from '@innovyze/lib-am-common/Selectors';
import { changeRiskConfigsPage } from '@innovyze/lib-am-common/Actions';
import { useGlobalization } from '@Translations/useGlobalization';
import {
	formatDateTimeWithFormatDate,
	useWebSockets,
} from '@innovyze/lib-am-common/Utils';
import { InspectionStandardType } from '@Types/InspectionStandardType';
import { WebSocketData } from '@Types/webSocket.types';
import { ResolvedResponse, Row } from '@innovyze/lib-am-common';
import FullRiskPage from './FullRiskPage';
import {
	getAllAssetsSchemaAction,
	assetSchemaSelectorByType,
} from '@innovyze/lib_asset_schema_store';
import { useNoConditionData } from '@Hooks/useNoConditionData';
import { useIsRiskTemplatesEnabled } from '@Hooks/useIsRiskTemplatesEnabled';
import { getRiskTabRoute, getRiskLandingRoute } from '@Utils/riskTabOrder';
import { withDisplayAssetId } from '@Utils/riskAssetId';

export const RiskManagement = ({ page }: { page?: number }) => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const params: { configId: string } = useParams();
	const isRiskDeteriorationModelEnabled = useIsFeatureEnabled(
		'info360-risk-enable-deterioration-model-results',
	);
	const history = useHistory();
	const location = useLocation<{ pageIndex: number }>();
	const pageIndex = location.state?.pageIndex || page;
	const isRiskTemplatesEnabled = useIsRiskTemplatesEnabled();
	const hasViewerAccess = viewerAccess(getSubscriptions());
	const pendingLandingDecisionRef = useRef(false);
	const { token } = useAuth0();
	const { formatDate } = useCompanyDateTime();
	const isRiskSetupDirty = selectIsRiskSetupDirty();
	const enablePreview = selectEnablePreview();
	const didMatrixUpdate = selectDidMatrixUpdate();

	const [forLoF, setForLoF] = useState(true);
	const [forInspection, setForInspection] = useState(true);
	const [cat, setCat] = useState<Category>();
	const [isNew, setNew] = useState(true);
	const [step2, setStep2] = useState<boolean>(false);
	const [component, setComponent] = useState<Component>({
		name: '',
		select: false,
		weighting: 0,
		type: '',
		metadata: undefined,
		metadataType: undefined,
		maxDistance: undefined,
		field: undefined,
		fieldAlias: undefined,
		nullScore: undefined,
	});
	const tab = selectRiskTab();
	const lofStore = selectLofStore();
	const cofStore = selectCofStore();
	// Only gated on categories, not on the separate field-metadata fetch:
	// AddEditComponent's Create/Update button is independently disabled until
	// a real field is selected (validConfig, requires field.length !== 0),
	// which is impossible while componentFields is still empty — so opening
	// the dialog before fields resolve can't produce a saved, unbound
	// component. Gating this on isLoadingFields as well blocks the dialog for
	// longer than the field fetch actually needs and breaks existing e2e flows
	// that open Add Component as soon as categories render.
	const isLoFLoading = lofStore.isLoadingCategories;
	const isCoFLoading = cofStore.isLoadingCategories;
	const riskStore = selectRiskStore();
	const calcStatus = selectRiskStatus();
	const lastRun = selectRiskLastRun();
	const riskPage = selectRiskConfigsPage();
	const riskConfig = selectRiskConfig();
	const riskSetting = selectRiskSetting();
	const riskMatrixRanges = selectUpdatedRiskRanges();
	const riskMatrixGrid = selectUpdatedRiskMatrixGrid();
	const isRiskRunning = selectRiskRunning();
	const [openCancel, setOpenCancel] = useState(false);
	const [isRiskRunCancelling, setIsRiskRunCancelling] = useState(false);
	const overallRisk = selectOverallRisk();

	const [editName, setEditName] = useState(false);
	const [completeLoad, setCompleteLoad] = useState(false);

	const noConditionData = useNoConditionData(
		riskStore.riskConfig.riskSetting.assetType,
	);
	//Feature flags

	const multipleModels = !!useIsFeatureEnabled(
		'info-360-asset-multiple-risk-models',
	);

	const websocketRiskStatus = useIsFeatureEnabled(
		'info-360-websocket-risk-status',
	);

	const websocketRiskScore = useIsFeatureEnabled(
		'info-360-websocket-risk-score',
	);

	/* eslint-disable-next-line */
	const updateStatusWebsocket = (data: any) => {
		// console.log('updateStatusWebsocket', data);
		const status = data.payload.calcStatus;
		const previousStatus = data.payload.previousStatus;
		// The connection may still be subscribed to a model opened earlier.
		// A status for another model only gets the toast — never this page's
		// run state, reloads or recordRunCompleted.
		const messageConfigId = resolveStatusConfigId(data);
		if (messageConfigId && messageConfigId !== params.configId) {
			const toast = runStatusToastAction(status, previousStatus);
			if (toast) dispatch(toast);
			return;
		}
		updateStatus(status, previousStatus);
	};
	const updateStatus = (
		status: RiskCalcStatusResult,
		previousStatus?: RiskCalcStatusResult,
	) => {
		// console.log('updateStatusWebsocket', status, previousStatus);
		let result = '';
		let running = false;

		if (status) {
			switch (status) {
				case 'risk-calc-complete':
				case 'risk-calc-failed':
				case 'risk-calc-cancelled':
					result = t('Risk Service: Ready');

					// If we have changed state then display a snackbar the once
					if (
						previousStatus != undefined &&
						previousStatus != 'risk-calc-complete' &&
						previousStatus != 'risk-calc-failed' &&
						previousStatus != 'risk-calc-cancelled'
					) {
						if (status === 'risk-calc-complete') {
							dispatch(success(t('Risk calculation completed')));
							dispatch(setRunCompleteReload(true));
							dispatch(recordRunCompleted(params.configId));
						} else if (status === 'risk-calc-cancelled') {
							dispatch(error(t('Risk calculation cancelled')));
						} else {
							dispatch(error(t('Risk calculation failed')));
						}
						dispatch(getLog(params.configId));
						dispatch(clearRiskScanId());
						dispatch(reloadRiskDetails(params.configId));
						dispatch(setRefreshList(true));
						dispatch(setSearchStart(false));

						// Do this in case another client has clicked cancel for your run
						setIsRiskRunCancelling(false);
					}
					break;
				case 'risk-calc-initiated':
				case 'risk-calc-start':
					result = t('Risk Service: In Progress');
					running = true;
					break;
				case 'risk-calc-getting-assets':
					result = t('Risk Service: Getting Assets');
					running = true;
					break;
				case 'risk-calc-getting-inspections':
					result = t('Risk Service: Getting Inspections');
					running = true;
					break;
				case 'risk-calc-getting-risk-config':
					result = t('Risk Service: Gettting Risk Configuration');
					running = true;
					break;
				case 'risk-calc-in-progress':
				case 'risk-calc-post-processing-normalize':
					result = t('Risk Service: In Progress');
					running = true;
					break;
				case 'risk-calc-writing-to-snowflake':
					result = t('Risk Service: Writing Results');
					running = true;
					break;
				case 'risk-calc-archiving-existing-in-snowflake':
					result = t('Risk Service: Archiving Results');
					running = true;
					break;
				// case 'error':
				// 	result = 'Risk Service: Unable to connect';
				// 	break;
			}
		}

		dispatch(setRiskStatus(result));
		dispatch(setRiskRunning(running));
		return true;
	};

	const pollRiskCalcStatus = async (cancel: boolean): Promise<void> => {
		if (cancel) await RiskCalcStatus.cancelPollForCalcStatus();
		else
			await RiskCalcStatus.pollForCalcStatus(
				token || '',
				params.configId,
				updateStatus,
			);
	};
	/* eslint-disable-next-line */
	const riskCalcStatusErrorCallBack = (data: any) => {
		console.log('riskCalcStatusErrorCallBack', data);
	};

	const webSocket = useWebSockets();
	// Registered through useWebSocketRoute so status messages always reach this
	// page's latest updateStatus (current configId), not the first model's.
	// Declared before the effect below so the route exists before we subscribe.
	useWebSocketRoute(
		'riskCalcStatus',
		updateStatusWebsocket,
		riskCalcStatusErrorCallBack,
		!!websocketRiskStatus,
		// After leaving the model (e.g. back to the list) still show the
		// finished/failed/cancelled toast, but skip the model-specific reloads.
		(data: WebSocketData) => {
			const toast = runStatusToastAction(
				data.payload?.calcStatus,
				data.payload?.previousStatus,
			);
			if (toast) dispatch(toast);
		},
	);
	useEffect(() => {
		if (websocketRiskStatus) {
			webSocket.sendMessage(
				JSON.stringify({
					path: 'risk',
					action: 'riskCalcStatus',
					configId: params.configId,
				}),
			);
		}
	}, [params.configId]);

	useEffect(() => {
		// This is to stop it reloading everytime the tab changes
		if (riskStore.riskConfig.riskSetting.configId !== params.configId) {
			dispatch(getRiskConfig(params.configId));
			if (!websocketRiskStatus) pollRiskCalcStatus(false);

			if (isRiskTemplatesEnabled) {
				// riskSetting still holds the previous model's data here —
				// getRiskConfig hasn't resolved yet, so status would reflect
				// the wrong model. Defer the run-status landing decision to
				// the effect below, which fires once the new config lands.
				pendingLandingDecisionRef.current = true;
			} else if (tab != 'details') {
				// If we have a new config ID and we aren't on the details tab switch to it
				history.push(addRouteNamespace(`results/${params.configId}`));
			}
		}
		// Also runs when config was hydrated before navigation (e.g. template apply).
		setCompleteLoad(true);
		dispatch(getAllAssetsSchemaAction());
	}, []);

	useEffect(() => {
		if (
			pendingLandingDecisionRef.current &&
			riskStore.riskConfig.riskSetting.configId === params.configId
		) {
			pendingLandingDecisionRef.current = false;
			// status (not lastRunCompleted, which the backend falls back to
			// LastRun for when a run failed/was cancelled/never finished —
			// see Risk.sagas.ts's mapMongoCfgToSnowCfg) is the one signal
			// that's actually false for a run that didn't complete.
			const hasBeenRun =
				riskStore.riskConfig.riskSetting.status ===
				'risk-calc-complete';
			// Whoever navigated here (RiskListCard, create/duplicate) already
			// picked the right tab using its own non-stale data, so this is
			// usually a no-op check — only push if that initial guess turns
			// out to be wrong.
			const landingRoute = getRiskLandingRoute(
				hasBeenRun,
				hasViewerAccess,
			);
			const target = addRouteNamespace(
				`${landingRoute}/${params.configId}`,
			);
			if (location.pathname !== target) {
				history.push(target);
			}
		}
	}, [
		riskStore.riskConfig.riskSetting.configId,
		riskStore.riskConfig.riskSetting.status,
		location.pathname,
	]);

	useEffect(() => {
		if (!isRiskRunning) {
			setIsRiskRunCancelling(false);
		}
	}, [isRiskRunning]);

	const allComponents = [
		...riskStore.riskConfig.LOFCategories,
		...riskStore.riskConfig.COFCategories,
	].flatMap(cat => cat.components);

	const usesFailureDefinition = allComponents.some(
		comp => comp.type === ComponentType.FailureDefinition,
	);
	const usesDeteriorationModel = allComponents.some(
		comp => comp.type === ComponentType.DeteriorationModel,
	);

	const assetSchema = useSelector(state =>
		assetSchemaSelectorByType(
			state,
			riskStore.riskConfig.riskSetting.assetType,
		),
	);

	const adpAttrs = decoratedAttributes({
		riskname: riskStore.riskConfig.riskSetting.name,
		riskid: riskStore.riskConfig.riskSetting.configId,
		assettype: riskStore.riskConfig.riskSetting.assetType,
		systemtype: assetSchema?.systemType?.id ?? '',
		usesfailuredefinition: usesFailureDefinition ? 'yes' : 'no',
		usesdeteriorationmodel: usesDeteriorationModel ? 'yes' : 'no',
	});

	const riskScoreCallBack = async (data: WebSocketData) => {
		// const key = data.payload.key;
		const response = data.payload;
		// const requestId = data.requestId;
		// const connectionId = data.connectionId;
		if (response.data && response.data.length > 0) {
			const resolvedResponse: ResolvedResponse = {
				pagination: response.pagination,
				data: response.data,
				scanId: response.scanId,
			};
			const rows = resolvedResponse.data as Row[];
			resolvedResponse.data = rows.map(riskDetail =>
				withDisplayAssetId(riskDetail as Row & { ASSET_ID: string }),
			);
			dispatch(
				getRiskDetailsWSReseived({
					searchStr: data.requestBody.queryParameters.search,
					response: resolvedResponse,
				}),
			);
		} else {
			dispatch(
				getRiskDetailsRejected({
					code: 500,
					text: 'Failed to get risk details',
				}),
			);
			dispatch(setRiskDetailsIsBusy(false));
			dispatch(setSearchStart(false));
		}
	};
	const riskScoreErrorCallBack = async () => {
		dispatch(
			getRiskDetailsRejected({
				code: 500,
				text: 'Failed to get risk details',
			}),
		);
		dispatch(setRiskDetailsIsBusy(false));
		dispatch(setSearchStart(false));
	};

	const query = selectRiskDetailsQueryObj();
	const startSearch = selectRiskDetailsStartSearch();
	const searchString = selectRiskDetailsSearch();

	const refreshRiskScore = () => {
		if (websocketRiskScore) {
			webSocket.setupRoutes(
				'riskScore',
				riskScoreCallBack,
				riskScoreErrorCallBack,
			);
			dispatch(setRiskDetailsIsBusy(true));
			dispatch(clearRiskScanId());
			webSocket.sendMessage(
				JSON.stringify({
					path: 'risk',
					action: 'riskScore',
					queryParameters: query,
				}),
			);
		} else {
			dispatch(getRiskDetailsDefaulted());
		}
	};

	useEffect(() => {
		if (startSearch !== 0) {
			refreshRiskScore();
		}
	}, [searchString]);

	useEffect(() => {
		// This so we dont try and load everything at once
		if (
			completeLoad &&
			riskStore.riskConfig.riskSetting.configId === params.configId
		) {
			setCompleteLoad(false);
			dispatch(setSearchStart(true));
			refreshRiskScore();
			dispatch(getLog(params.configId));
			dispatch(
				getLoFInspectionFields({
					assetType: riskStore.riskConfig.riskSetting.assetType,
					inspectionStandard:
						riskStore.riskConfig.riskSetting.inspectionStandard,
				}),
			);
			dispatch(getLoFFields(riskStore.riskConfig.riskSetting.assetType));
			dispatch(getCoFFields(riskStore.riskConfig.riskSetting.assetType));
			dispatch(getSimulationData());
			dispatch(getTaskData(riskStore.riskConfig.riskSetting.assetType));
			if (isRiskDeteriorationModelEnabled) {
				dispatch(getDeteriorationModels());
				dispatch(getFailureDefinitions());
			}

			dispatch(getSpatialLayerList());
			dispatch(
				getProximityAttributeFields(
					riskStore.riskConfig.riskSetting.assetType,
				),
			);
			dispatch(
				getCustomTableFields(
					riskStore.riskConfig.riskSetting.assetType,
				),
			);
		}
	}, [completeLoad, riskStore]);

	const onAddComponent = (
		forLoF: boolean,
		forInspection: boolean,
		category: Category | undefined,
	) => {
		if ((forLoF && !isLoFLoading) || (!forLoF && !isCoFLoading)) {
			if (category) setCat(category);
			setForLoF(forLoF);
			setForInspection(forInspection);
			setNew(true);
			setComponent({
				name: '',
				select: false,
				weighting: 0,
				type: '',
				metadata: undefined,
				metadataType: undefined,
				maxDistance: undefined,
				field: undefined,
				fieldAlias: undefined,
				nullScore: undefined,
			});
			setStep2(true);
		}
	};

	const onEditComponent = (
		forLoF: boolean,
		forInspection: boolean,
		category: Category | undefined,
		component: Component,
	) => {
		if (category) setCat(category);
		setForInspection(forInspection);
		setForLoF(forLoF);
		setNew(false);
		setComponent(component);
		setStep2(true);
	};

	const refresh = () => {
		dispatch(getRiskConfig(params.configId));
	};

	const saveCLOFConfig = () => {
		if (tab === 'lof') {
			dispatch(saveLoF({ refresh: refresh }));
		} else if (tab === 'cof') {
			dispatch(saveCoF({ refresh: refresh }));
		}
		dispatch(setRefreshList(true));
	};

	const isConfigValid = () => {
		if (tab === 'lof') {
			const categories = lofStore.categories;
			if (!categories || categories.length === 0) return false;

			// For Risk Models with noConditionData they can have just the single fake Condition Category and still be valid
			if (noConditionData && categories.length == 1) return true;

			const categoriesTotalWeight = categories
				.filter(category => !category.useThisOnly)
				.map(category => category.weighting)
				.reduce((prev, current) => prev + current, 0);
			if (
				categoriesTotalWeight !== 100 &&
				categories.filter(category => !category.useThisOnly).length > 0
			)
				return false;
			for (const category of categories) {
				if (
					!category.useHighestScore &&
					category.weighting !== 0 &&
					category.components &&
					category.components.length != 0
				) {
					// Total of 0 or 100 from active componments is valid
					const total = category.components
						.map(component =>
							component.select ? component.weighting : 0,
						)
						.reduce((prev, current) => prev + current, 0);
					if (total !== 0 && total !== 100) return false;
				}
			}

			return true;
		} else if (tab === 'cof') {
			const categories = cofStore.categories;
			if (!categories) return false;
			const categoriesTotalWeight = categories
				.map(category => category.weighting)
				.reduce((prev, current) => prev + current, 0);
			if (categoriesTotalWeight !== 100 && categories.length > 0)
				return false;
			for (const category of categories) {
				if (
					!category.useHighestScore &&
					category.weighting !== 0 &&
					category.components &&
					category.components.length != 0
				) {
					// Total of 0 or 100 from active componments is valid
					const total = category.components
						.map(component =>
							component.select ? component.weighting : 0,
						)
						.reduce((prev, current) => prev + current, 0);
					if (total !== 0 && total !== 100) return false;
				}
			}

			return true;
		}
	};

	const canBeSaved = () => {
		if (tab === 'lof') {
			return lofStore.isChanged && isConfigValid();
		} else if (tab === 'cof') {
			return cofStore.isChanged && isConfigValid();
		}

		return false;
	};

	const getLastRun = (lastRun: string) => {
		if (lastRun != undefined && lastRun != '') {
			return t('Last Run was {{date}}', {
				date: formatDateTimeWithFormatDate(lastRun, formatDate),
			});
		} else return '';
	};

	useEffect(() => {
		dispatch(
			setRiskLastRun(
				getLastRun(riskStore?.riskConfig.riskSetting.LastRun),
			),
		);
	}, [riskStore?.riskConfig.riskSetting.LastRun]);

	const onPreviewClicked = () => {
		if (riskSetting.matrix) {
			const previewObjPyld = {
				cofWeighting: overallRisk.cofWeighting,
				lofWeighting: overallRisk.lofWeighting,
				riskMethod: overallRisk.riskMethod,
				grades: riskMatrixRanges,
				grid: riskMatrixGrid,
				preview: {
					grades: riskMatrixRanges,
					grid: riskMatrixGrid,
				},
				configId: riskSetting.configId,
				initialLoad: true,
			};
			dispatch(initRiskMatrixPreview(previewObjPyld));
		}
	};

	const onPreviewCancelClicked = () => {
		if (riskSetting.matrix) {
			const matrix = {
				grades: riskMatrixRanges,
				grid: riskMatrixGrid,
				preview: {
					grades: riskMatrixRanges,
					grid: riskMatrixGrid,
				},
			};
			dispatch(
				cancelRiskMatrixPreview({
					configId: riskSetting.configId,
					matrix,
				}),
			);
		}
	};

	const onRunClicked = () => {
		dispatch(runRisk(params.configId));
		dispatch(setRefreshList(true));
	};

	const onCancelClicked = () => {
		setIsRiskRunCancelling(true);
		setOpenCancel(true);
	};

	// This stops the dialog closing when clicked on the background
	const onCloseDialog = () => void {};

	const dialogProps: DialogProps = {
		open: openCancel,
		onClose: onCloseDialog,
	};

	const handleCancelConfirmYes = () => {
		dispatch(resetRisk(params.configId));
		setOpenCancel(false);
	};

	const handleCancelConfirmNo = () => {
		setIsRiskRunCancelling(false);
		setOpenCancel(false);
	};

	const onSaveRiskClicked = () => {
		if (didMatrixUpdate) {
			//if Risk Matrix was updated
			const oldRiskConfig = { ...riskConfig };
			const oldRiskSetting = { ...riskSetting };
			const newMatrix = {
				grades: { ...riskStore.riskMatrix.grades },
				grid: [...riskStore.riskMatrix.grid],
			};
			const newSetting = { ...oldRiskSetting, matrix: newMatrix };
			const newRiskConfig: RiskConfig = {
				...oldRiskConfig,
				riskSetting: newSetting,
			};
			dispatch(saveRiskConfigAction(newRiskConfig));
		}
		dispatch(setOnlyGradesUpdated(false));
		dispatch(setDidMatrixUpdate(false));
		dispatch(setRiskSetupDirty(false));
		dispatch(setMatrixPreviewStatus(''));
		dispatch(setSaveRun(true));
		dispatch(setRefreshList(true));
	};

	const onEditName = () => {
		setEditName(true);
	};

	const onEditNameClose = (saved: boolean) => {
		setEditName(false);
		if (saved) {
			dispatch(changeRiskConfigsPage(riskPage.page));
		}
	};

	const getActionButton = () => {
		if (
			enablePreview &&
			!riskStore.isOnlyGradesUpdated &&
			!riskStore.isPreviewLoading
		) {
			return (
				<Button
					key="button-preview"
					dataCy="button-preview"
					disabled={riskStore.isLoading}
					variant="contained"
					color="primary"
					style={{
						height: '36px',
						marginLeft: '12px',
						width: '100%',
					}}
					onClick={onPreviewClicked}>
					{t('Preview')}
				</Button>
			);
		} else if (riskStore.isPreviewLoading) {
			return (
				<Button
					key="button-cancel-preview"
					dataCy="button-cancel-preview"
					variant="contained"
					color="error"
					style={{
						height: '36px',
						marginLeft: '12px',
						width: '100%',
					}}
					onClick={onPreviewCancelClicked}>
					{t('Cancel Preview')}
				</Button>
			);
		} else if (isRiskSetupDirty) {
			return (
				<Button
					key="button-save"
					dataCy="button-save"
					disabled={riskStore.isLoading}
					variant="contained"
					color="primary"
					style={{
						height: '36px',
						marginLeft: '12px',
						width: '100%',
					}}
					onClick={onSaveRiskClicked}
					adp-click="true"
					adp-component-id="save-risk-config"
					adp-component-name="Save Risk Config"
					{...adpAttrs}>
					{t('Save')}
				</Button>
			);
		} else if (isRiskRunning) {
			return (
				<Button
					key="button-cancel"
					dataCy="button-cancel"
					variant="contained"
					color="error"
					disabled={isRiskRunCancelling}
					style={{
						height: '36px',
						marginLeft: '12px',
						width: '100%',
					}}
					onClick={onCancelClicked}>
					{t('Cancel Run')}
				</Button>
			);
		} else {
			return (
				<Button
					key="button-run"
					dataCy="button-run"
					disabled={riskStore.isLoading}
					variant="contained"
					color="primary"
					style={{
						height: '36px',
						marginLeft: '12px',
						width: '100%',
					}}
					onClick={onRunClicked}
					adp-click="true"
					adp-component-id="run-risk-config"
					adp-component-name="Run Risk Config"
					{...adpAttrs}>
					{t('Run')}
				</Button>
			);
		}
	};

	const getSecondary = () => {
		if (tab === 'lof' || tab === 'cof') {
			return (
				<Grid container width="350px" style={{ textAlign: 'end' }}>
					<Grid item xs={7} style={{ alignSelf: 'center' }}>
						<Text variant="caption-small">{lastRun}</Text>
						<Text>{calcStatus}</Text>
					</Grid>
					<Grid item xs={5}>
						<Button
							disabled={!canBeSaved()}
							variant="contained"
							color="primary"
							style={{
								height: '36px',
								marginLeft: '12px',
								width: '100%',
							}}
							onClick={saveCLOFConfig}
							adp-click="true"
							adp-component-id="save-risk-config"
							adp-component-name="Save Risk Config"
							{...adpAttrs}>
							{t('Save')}
						</Button>
					</Grid>
				</Grid>
			);
		} else if (tab === 'risk') {
			return (
				<Grid container width="350px" style={{ textAlign: 'end' }}>
					<Grid item xs={7} style={{ alignSelf: 'center' }}>
						<Text variant="caption-small">{lastRun}</Text>
						<Text>{calcStatus}</Text>
					</Grid>
					<Grid item xs={5}>
						{getActionButton()}
					</Grid>
				</Grid>
			);
		} else {
			return (
				<Grid container width="350px" style={{ textAlign: 'end' }}>
					<Grid item xs={12} style={{ alignSelf: 'center' }}>
						<Text variant="caption-small">{lastRun}</Text>
						<Text>{calcStatus}</Text>
					</Grid>
				</Grid>
			);
		}
	};

	const beforeTabChange = (event: CancellableEvent): CancellableEvent => {
		event.cancelled = true;
		const routeKey = getRiskTabRoute(event.toIndex, isRiskTemplatesEnabled);
		if (routeKey) {
			history.push(addRouteNamespace(`${routeKey}/${params.configId}`));
		}
		return event;
	};

	const back = () => {
		if (!websocketRiskStatus) pollRiskCalcStatus(true);
		history.push('/risk/');
	};

	return (
		<>
			<WarningDialog
				dialogProps={dialogProps}
				data-cy="cancel-dialog"
				open={openCancel}
				title={t('Cancel Risk Run?')}
				content={t('Are you sure you want to cancel the risk run?')}
				cancelText={t('No')}
				cancelButtonProps={{
					dataCy: 'cancel-cancel',
				}}
				confirmText={t('Cancel Run')}
				confirmButtonProps={{
					dataCy: 'cancel-confirm',
				}}
				onCancel={handleCancelConfirmNo}
				onConfirm={handleCancelConfirmYes}
			/>

			{step2 ? (
				<>
					<AddEditComponent
						countKey={
							isNew
								? 'NewConfig'
								: `${params.configId}-${component.type}-${
										forLoF ? 'lof' : 'cof'
								  }-${component.name}`
						}
						forLoF={forLoF}
						observations={
							(forLoF &&
								riskStore.riskConfig.riskSetting
									.inspectionStandard ===
									InspectionStandardType.PACP) ||
							riskStore.riskConfig.riskSetting
								.inspectionStandard ===
								InspectionStandardType.MACP
						}
						inspectionType={
							forInspection
								? riskStore.riskConfig.riskSetting
										.inspectionStandard
								: undefined
						}
						isNew={isNew}
						assetType={riskStore.riskConfig.riskSetting.assetType}
						assetFilterId={
							riskStore.riskConfig.riskSetting.assetFilterId
						}
						category={cat}
						component={component}
						onApply={() => {
							setStep2(false);
						}}
						onCancel={() => {
							setStep2(false);
						}}
					/>
					<NavigationPrompt
						title={t('Add New Component')}
						content={t(
							'Navigating away from this page will cancel the new component. Do you want to continue to navigate away from this page?',
						)}
						cancelText={t('No')}
						confirmText={t('Yes')}
						when={true}
						shouldBlockReload={true}
						navigate={nextLocation => {
							history.push(nextLocation);
						}}
						shouldBlockNavigation={() => true}
					/>
				</>
			) : (
				<FullRiskPage
					getSecondary={getSecondary}
					back={back}
					multipleModels={multipleModels}
					beforeTabChange={beforeTabChange}
					pageIndex={pageIndex ?? 0}
					refreshRiskScore={refreshRiskScore}
					onAddComponent={onAddComponent}
					onEditComponent={onEditComponent}
					onEditName={onEditName}
					t={t}
				/>
			)}

			<RenameRisk open={editName} onClose={onEditNameClose} />
		</>
	);
};

export default RiskManagement;
